<template>
    <Dialog v-model:open="open">
        <DialogContent class="sm:max-w-[425px]">
            <DialogHeader>
                <DialogTitle>Add Cloudflare Server</DialogTitle>
            </DialogHeader>
            <ScrollArea class="h-[calc(75vh)] p-1">
                <div class="space-y-3">
                    <FormField name="title" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Title (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="Title" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="description" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Description (Optional)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="Description" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="db_name" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>DB Name (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="DB Name" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="db_host" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>DB Host (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="DB Host" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="db_driver" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>DB Driver (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="DB Driver" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="db_username" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>DB Username (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="DB Username " />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="db_port" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>DB Port (Required)</FormLabel>
                            <FormControl>
                                <Input
                                    type="number"
                                    v-bind="componentField"
                                    placeholder="DB Port"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="db_password" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>DB Password (Required)</FormLabel>
                            <FormControl>
                                <Password
                                    v-bind="componentField"
                                    placeholder="DB Port"
                                    :can-generate="false"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="api_token" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>API Token (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="API Token" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="api_url" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>API URL (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="API URL" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
            </ScrollArea>
            <DialogFooter>
                <Button type="button" variant="secondary" :disabled="isCreatePending" @click="close"
                    >Cancel</Button
                >
                <Button
                    type="button"
                    :loading="isCreatePending"
                    :disabled="!meta.valid || !meta.dirty"
                    @click="submit"
                    >Save</Button
                >
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import Password from '@/components/ui/input/Password.vue'
import ScrollArea from '@/components/ui/scroll-area/ScrollArea.vue'
import { setFieldsError } from '@/lib/utils'
import { useAddCloudflareServerMutation } from '@/queries/cloudflare'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { computed } from 'vue'
import { z } from 'zod'

const open = defineModel()

const { mutate: addServer, isPending: isCreatePending } = useAddCloudflareServerMutation()

const schema = z.object({
    title: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    description: z.string().nullish(),
    db_name: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    db_host: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    db_driver: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    db_username: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    db_port: z
        .number()
        .int()
        .positive({
            message: 'Port must be a positive integer.',
        })
        .min(1, {
            message: 'Port must be at least 1.',
        })
        .max(65535, {
            message: 'Port must be at most 65535.',
        }),
    db_password: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    api_url: z
        .string()
        .min(1, {
            message: "You can't leave this empty.",
        })
        .url({
            message: 'Url must be a valid URL.',
        }),
    api_token: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
})

const form = useForm({
    validationSchema: toTypedSchema(schema),
})

const meta = computed(() => form.meta.value)

const submit = form.handleSubmit(async (values) => {
    if (!meta.value.dirty || !meta.value.valid) return

    addServer(values, {
        onSuccess: () => {
            close()
        },
        onError: (error) => {
            console.log(error)
            setFieldsError(error, form.setFieldError)
        },
    })
})

const close = () => {
    open.value = false
    form.resetForm()
}
</script>
